import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { useMemo, useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated from 'react-native-reanimated'
import Icon from '~/components/Icon'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { SectionContent } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { ScrollView } from '~/components/ScrollView'
import { Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Badge } from '~/components/ui/badge'
import { TabBar, TabContent, Tabs, type TabItem } from '~/components/ui/tabs'
import { Text } from '~/components/ui/text'
import { useMySentContactRequests } from '~/data/contacts/useContacts'
import { useMyOwnedLeads } from '~/data/leads/useLeads'
import { cn } from '~/lib/utils'

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

type MyLeadsTabId = 'owned' | 'requests'

export default function MyLeads() {
  const { t } = useTranslations('leads')
  const [activeTab, setActiveTab] = useState<MyLeadsTabId>('owned')

  const { data: ownedLeads, isLoading: isLoadingOwned } = useMyOwnedLeads()
  const { data: sentRequests, isLoading: isLoadingRequests } = useMySentContactRequests()

  const tabs: TabItem<MyLeadsTabId>[] = useMemo(
    () => [
      {
        id: 'owned',
        label: t(($) => $.myLeads.ownedTab),
        count: ownedLeads?.length ?? 0,
        animationOrigin: 'top-left',
        content: (
          <OwnedLeadsContent
            leads={ownedLeads ?? []}
            isLoading={isLoadingOwned}
            className="rounded-tl-none"
          />
        ),
      },
      {
        id: 'requests',
        label: t(($) => $.myLeads.requestsTab),
        count: sentRequests?.length ?? 0,
        animationOrigin: 'top-right',
        content: (
          <SentRequestsContent
            requests={sentRequests ?? []}
            isLoading={isLoadingRequests}
            className="rounded-tr-none"
          />
        ),
      },
    ],
    [ownedLeads, sentRequests, isLoadingOwned, isLoadingRequests, t]
  )

  return (
    <ScreenView>
      <ScrollView
        className="flex-1 rounded-content transition-opacity duration-150"
        contentContainerClassName="gap-0 sm:px-3">
        <Tabs<MyLeadsTabId> tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
          <TabBar />
          <TabContent />
        </Tabs>
      </ScrollView>
    </ScreenView>
  )
}

interface OwnedLead {
  id: string
  name: string
  language: string
  created_at: string
  is_own: boolean
  is_purchased: boolean
  interest_id: string | null
  verified_at: string | null
}

function OwnedLeadsContent({
  leads,
  isLoading,
  className,
}: {
  leads: OwnedLead[]
  isLoading: boolean
  className?: string
}) {
  const { t } = useTranslations('leads')
  const router = useRouter()

  if (isLoading) {
    return (
      <ContentBox
        className={className}
        contentContainerClassName="items-center justify-center py-8">
        <LoadingView />
      </ContentBox>
    )
  }

  if (leads.length === 0) {
    return (
      <ContentBox className={className} contentContainerClassName="py-8">
        <SectionContent textClassName="text-center text-lg/relaxed">
          {t(($) => $.myLeads.noOwnedLeads)}
        </SectionContent>
      </ContentBox>
    )
  }

  return (
    <ContentBox
      scrollable={false}
      className={cn('min-h-sm', className)}
      contentContainerClassName="gap-3 px-2 py-6">
      {leads.map((lead) => (
        <AnimatedPressable
          key={lead.id}
          onPress={() =>
            router.push({
              pathname: '/leads/[leadId]',
              params: { leadId: lead.id },
            })
          }
          className="group/button mx-3 flex-row items-center gap-4 rounded-xl border border-border bg-background p-4 transition-all duration-150 hover:bg-muted/50 active:scale-[0.98] active:bg-muted/50">
          <View className="size-12 items-center justify-center rounded-full bg-success/10">
            <Icon name="person-outline" size={24} className="text-success" />
          </View>
          <View className="flex-1 gap-1">
            <Heading2 className="font-medium text-base" numberOfLines={1}>
              {lead.name}
            </Heading2>
            <Label className="text-sm text-secondary">
              {t(($) => $.myLeads.commissionEligible)}
            </Label>
          </View>
          <Icon name="chevron-forward" size={20} className="text-secondary" />
        </AnimatedPressable>
      ))}
    </ContentBox>
  )
}

interface SentRequest {
  id: string
  status: 'pending' | 'accepted' | 'rejected'
  created_at: string
  sent_at: string | null
  responded_at: string | null
  contact_id: string | null
  purchased_contact_id: string | null
  assets: { id: string; name: string; media: unknown } | null
  contacts: { name: string } | null
}

function SentRequestsContent({
  requests,
  isLoading,
  className,
}: {
  requests: SentRequest[]
  isLoading: boolean
  className?: string
}) {
  const { t } = useTranslations('leads')
  const router = useRouter()

  if (isLoading) {
    return (
      <ContentBox
        className={className}
        contentContainerClassName="items-center justify-center py-8">
        <LoadingView />
      </ContentBox>
    )
  }

  if (requests.length === 0) {
    return (
      <ContentBox className={className} contentContainerClassName="py-8">
        <SectionContent textClassName="text-center text-lg/relaxed">
          {t(($) => $.myLeads.noSentRequests)}
        </SectionContent>
      </ContentBox>
    )
  }

  return (
    <ContentBox
      scrollable={false}
      className={cn('min-h-sm', className)}
      contentContainerClassName="gap-3 px-2 py-6">
      {requests.map((request) => {
        const leadId = request.purchased_contact_id ?? request.contact_id

        return (
          <AnimatedPressable
            key={request.id}
            onPress={() => {
              if (leadId) {
                router.push({
                  pathname: '/leads/[leadId]',
                  params: { leadId },
                })
              }
            }}
            className={cn(
              'group/button mx-3 flex-row items-center gap-4 rounded-xl border border-border bg-background p-4 transition-all duration-150 hover:bg-muted/50 active:scale-[0.98] active:bg-muted/50',
              !leadId && 'opacity-60'
            )}>
            <View
              className={cn(
                'size-12 items-center justify-center rounded-full',
                request.status === 'accepted' && 'bg-success/10',
                request.status === 'rejected' && 'bg-destructive/10',
                request.status === 'pending' && 'bg-warning/10'
              )}>
              <Icon
                name={
                  request.status === 'accepted'
                    ? 'checkmark-circle-outline'
                    : request.status === 'rejected'
                      ? 'close-circle-outline'
                      : 'time-outline'
                }
                size={24}
                className={cn(
                  request.status === 'accepted' && 'text-success',
                  request.status === 'rejected' && 'text-destructive',
                  request.status === 'pending' && 'text-warning'
                )}
              />
            </View>
            <View className="flex-1 gap-1">
              <View className="flex-row items-center gap-2">
                <Heading2 className="font-medium text-base" numberOfLines={1}>
                  {request.contacts?.name ?? t(($) => $.verification.verifiedContact)}
                </Heading2>
                <ContactRequestStatusBadge status={request.status} />
              </View>
              {request.assets?.name && (
                <Text className="text-sm text-secondary" numberOfLines={1}>
                  {request.assets.name}
                </Text>
              )}
            </View>
            <Icon name="chevron-forward" size={20} className="text-secondary" />
          </AnimatedPressable>
        )
      })}
    </ContentBox>
  )
}

function ContactRequestStatusBadge({ status }: { status: 'pending' | 'accepted' | 'rejected' }) {
  const { t } = useTranslations('leads')

  const variant =
    status === 'accepted' ? 'success' : status === 'rejected' ? 'destructive' : 'warning'

  const label =
    status === 'accepted'
      ? t(($) => $.myLeads.statusAccepted)
      : status === 'rejected'
        ? t(($) => $.myLeads.statusRejected)
        : t(($) => $.myLeads.statusPending)

  return (
    <Badge variant={variant} size="xs">
      {label}
    </Badge>
  )
}
